import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BadgeComponent } from 'shared/components/BadgeComponent';
import { BadgeGroupsComponent } from 'shared/components/BadgeGroupsComponent';
import { CreateProfileButton } from 'features/profiles/CreateProfileButton';
import { SlidingButtonLabel } from 'shared/components/SlidingButtonLabel';
import { useSlackAuth } from 'features/auth/SlackAuthContext';
import { useTalosData } from 'shared/context/TalosDataContext';
import { useBadgeEditing } from 'features/claim/useBadgeEditing';
import { Badge } from 'features/catalog/useBadges';
import { Profile } from 'features/profiles/useProfiles';
import { BadgeModes } from 'types/badge-modes';
import {
  getBadgeCollectionGroup,
  setBadgeCollectionGroup,
  subscribeBadgeCollectionGroup,
} from 'features/catalog/badge-collection-group';
import {
  BADGE_GROUPS,
  groupBackgroundGradient,
  groupNameForBadgeId,
  slugifyGroupName,
} from 'features/catalog/badge-groups';
import { isJourneyCompleted, JOURNEYS } from 'features/catalog/badge-journeys';
import { getPendingClaimBadgeId, setPendingClaimBadgeId } from 'features/claim/claim-prime';
import { getClipboardText } from 'features/claim/clipboard';
import { NEW_PASTE_URL } from 'config/data-sources';
import {
  EMPTY_EDIT_LAYOUT_FROZEN_PREFIX,
  EDIT_LAYOUT_TOP_SUGGESTIONS,
  extendEditLayoutFrozenPrefix,
  resolveEditProfileGroupLayout,
  type EditLayoutFrozenPrefix,
} from 'features/claim/edit-group-layout';
import { getFirstPartOfEmail } from 'shared/lib/email';
import { claimPath, SUMMARY_PATH } from 'shared/lib/nav-paths';
import { compareBadgesByScoreAsc, getRarityImageUrlByName, getRarityScoreFromName } from 'features/catalog/rarity';
import '../gallery/BadgeGalleryView.css';
import './ClaimBadgesView.css';

const EMPTY_BADGE_IDS: string[] = [];

/** Matches `.badge.smaller` / `.claim-badges-diff-more` width used to size the New badges grid. */
const NEW_BADGES_CELL_REM = 9;
/** Cap the New badges grid to this fraction of the viewport height. */
const NEW_BADGES_MAX_VIEWPORT_RATIO = 0.5;

type ClaimStep = 'edit' | 'confirm';

const writeClipboardText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }
};

interface ClaimBadgesViewProps {
  profile: Profile | null;
  setMode: (mode: BadgeModes) => void;
  onSelectBadge: (badge: Badge) => void;
  onSubmissionTriggered: (code: string) => void;
  onSaveDisabledChange: (disabled: boolean) => void;
  navigate: (path: string) => void;
  /** When set, enable this badge once on enter and stay on Summarize & Claim. */
  initialAutoClaimBadgeId?: string | null;
  onInitialAutoClaimConsumed?: () => void;
}

export const ClaimBadgesView: React.FC<ClaimBadgesViewProps> = ({
  profile,
  setMode,
  onSelectBadge,
  onSubmissionTriggered,
  onSaveDisabledChange,
  navigate,
  initialAutoClaimBadgeId = null,
  onInitialAutoClaimConsumed,
}) => {
  const { badgesMap, contributorIndex } = useTalosData();
  const { user } = useSlackAuth();
  const location = useLocation();
  const primedBadgeId = initialAutoClaimBadgeId || getPendingClaimBadgeId();
  const forceSummary = Boolean(primedBadgeId);
  const claimStep: ClaimStep = location.pathname === SUMMARY_PATH || forceSummary ? 'confirm' : 'edit';
  const editPath = claimPath(profile ? getFirstPartOfEmail(profile.email) : null);
  const defaultDisplayName = getFirstPartOfEmail(user?.email);
  const profileBadgeIDs = profile?.badgesIDs ?? EMPTY_BADGE_IDS;
  const [createTouched, setCreateTouched] = useState(false);
  const [activeGroup, setActiveGroupState] = useState(() => getBadgeCollectionGroup());
  const [badgeSearch, setBadgeSearch] = useState('');
  const badgeSearchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => subscribeBadgeCollectionGroup(() => setActiveGroupState(getBadgeCollectionGroup())), []);

  const setActiveGroup = useCallback((id: string) => {
    setBadgeCollectionGroup(id);
  }, []);

  const {
    enabledBadgesMap,
    setEnabledBadgesMap,
    enabledBadges,
    saveDisabled,
    setSaveDisabled,
    updateDisabled,
    createDisabled,
    displayName,
    setDisplayName,
    effectiveDisplayName,
    newlyAddedBadges,
    leaveEditModeKey,
    handleSharedBadgeToggle,
    badgeCreditsSubmissions,
    badgeDesignSubmissions,
  } = useBadgeEditing({
    mode: 'editing_profile',
    badgesMap,
    selectedProfile: profile,
    profileBadgeIDs,
    contributorIndex,
    defaultDisplayName,
    primeClaimBadgeId: primedBadgeId,
  });

  const clipboardText = useCallback(
    (name: string) => getClipboardText(name, enabledBadges, profileBadgeIDs),
    [enabledBadges, profileBadgeIDs]
  );

  useEffect(() => {
    onSaveDisabledChange(saveDisabled);
  }, [saveDisabled, onSaveDisabledChange]);

  const groupedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of BADGE_GROUPS) {
      for (const id of group.badgesIDs) ids.add(id);
    }
    return ids;
  }, []);

  const freeformIds = useMemo(() => [...badgesMap.keys()].filter((id) => !groupedIds.has(id)), [badgesMap, groupedIds]);

  const creditIds = useMemo(() => badgeCreditsSubmissions.map((b) => b.id), [badgeCreditsSubmissions]);
  const designIds = useMemo(() => badgeDesignSubmissions.map((b) => b.id), [badgeDesignSubmissions]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const syncMobileNav = () => {
      if (!mq.matches) return;
      const current = getBadgeCollectionGroup();
      if (current !== 'all') setActiveGroup('all');
    };
    mq.addEventListener('change', syncMobileNav);
    return () => mq.removeEventListener('change', syncMobileNav);
  }, [setActiveGroup]);

  const submittedBadgeCount = useMemo(() => new Set([...creditIds, ...designIds]).size, [creditIds, designIds]);

  // Groups at/above the last claimed category stay put; groups after it re-sort by claim %.
  const editGroupLayoutFreezeKey = `${user?.email ?? ''}:${profileBadgeIDs.join(',')}`;
  const [editLayoutFrozenPrefix, setEditLayoutFrozenPrefix] = useState<EditLayoutFrozenPrefix>(
    EMPTY_EDIT_LAYOUT_FROZEN_PREFIX
  );
  const editLayoutFrozenPrefixRef = useRef(editLayoutFrozenPrefix);
  editLayoutFrozenPrefixRef.current = editLayoutFrozenPrefix;

  useEffect(() => {
    editLayoutFrozenPrefixRef.current = EMPTY_EDIT_LAYOUT_FROZEN_PREFIX;
    setEditLayoutFrozenPrefix(EMPTY_EDIT_LAYOUT_FROZEN_PREFIX);
  }, [editGroupLayoutFreezeKey]);

  const editGroupLayout = useMemo(
    () =>
      resolveEditProfileGroupLayout(
        BADGE_GROUPS,
        badgesMap,
        profileBadgeIDs,
        enabledBadges,
        submittedBadgeCount,
        editLayoutFrozenPrefix
      ),
    [badgesMap, profileBadgeIDs, enabledBadges, submittedBadgeCount, editLayoutFrozenPrefix]
  );
  const editGroupLayoutRef = useRef(editGroupLayout);
  editGroupLayoutRef.current = editGroupLayout;

  const handleBadgeToggle = useCallback(
    (badge: Badge, fromSuggestion = false) => {
      const nextPrefix = extendEditLayoutFrozenPrefix(
        editGroupLayoutRef.current,
        fromSuggestion ? EDIT_LAYOUT_TOP_SUGGESTIONS : groupNameForBadgeId(badge.id),
        editLayoutFrozenPrefixRef.current
      );
      editLayoutFrozenPrefixRef.current = nextPrefix;
      setEditLayoutFrozenPrefix(nextPrefix);
      handleSharedBadgeToggle(badge);
    },
    [handleSharedBadgeToggle]
  );

  const sortedBadgeGroups = useMemo(
    () => [...editGroupLayout.claimableGroups, ...editGroupLayout.deferredGroups],
    [editGroupLayout]
  );

  const navItems = useMemo(() => {
    const items: {
      id: string;
      label: string;
      gradient?: string;
      percentage?: number;
      disabled?: boolean;
    }[] = [{ id: 'all', label: 'All' }];
    const pushGroup = (group: (typeof BADGE_GROUPS)[number]) => {
      const claimed = group.badgesIDs.filter((id) => enabledBadgesMap.get(id)).length;
      const percentage = group.badgesIDs.length === 0 ? 0 : Math.round((claimed / group.badgesIDs.length) * 100);
      items.push({
        id: slugifyGroupName(group.name),
        label: group.name,
        gradient: groupBackgroundGradient(group),
        percentage,
      });
    };
    for (const group of editGroupLayout.claimableGroups) pushGroup(group);
    if (freeformIds.length > 0) {
      const claimed = freeformIds.filter((id) => enabledBadgesMap.get(id)).length;
      items.push({
        id: 'freeform',
        label: 'More Badges',
        percentage: Math.round((claimed / freeformIds.length) * 100),
      });
    }
    for (const group of editGroupLayout.deferredGroups) pushGroup(group);
    return items;
  }, [enabledBadgesMap, freeformIds, editGroupLayout]);

  useEffect(() => {
    if (!navItems.some((item) => item.id === activeGroup)) {
      setActiveGroup('all');
    }
  }, [activeGroup, navItems, setActiveGroup]);

  const visibleGroups = useMemo(() => {
    if (activeGroup === 'all') return sortedBadgeGroups;
    if (activeGroup === 'freeform') return [];
    return sortedBadgeGroups.filter((group) => slugifyGroupName(group.name) === activeGroup);
  }, [activeGroup, sortedBadgeGroups]);

  const showFreeformBadges = activeGroup === 'all' || activeGroup === 'freeform';
  const showJourneyCompletingSection = !!profile && activeGroup === 'all';
  const showClaimedByOthersSection = activeGroup === 'all';
  const showRecentlyAddedSection = !!profile && activeGroup === 'all';

  const newlyCompletedJourneys = useMemo(() => {
    if (!newlyAddedBadges.length) return [];
    const currentBadgeIDs = [...enabledBadgesMap.entries()].filter(([, enabled]) => enabled).map(([id]) => id);
    return JOURNEYS.filter(
      (j) => !isJourneyCompleted(j, profileBadgeIDs) && isJourneyCompleted(j, currentBadgeIDs)
    ).sort((a, b) => getRarityScoreFromName(b.rarity) - getRarityScoreFromName(a.rarity));
  }, [enabledBadgesMap, profileBadgeIDs, newlyAddedBadges.length]);

  const scoreDiff = useMemo(() => {
    // Breakdown lines use ownership / completion counts; Before/After + section meta use pts.
    const statsForIds = (ids: readonly string[]) => {
      let badgePoints = 0;
      for (const id of ids) {
        const badge = badgesMap.get(id);
        if (badge) badgePoints += badge.rarityScore;
      }
      const owned = new Set(ids);
      let journeyPoints = 0;
      let journeyCount = 0;
      for (const journey of JOURNEYS) {
        if (isJourneyCompleted(journey, owned)) {
          journeyPoints += getRarityScoreFromName(journey.rarity);
          journeyCount += 1;
        }
      }
      return {
        badges: ids.length,
        journeys: journeyCount,
        badgePoints,
        journeyPoints,
        total: badgePoints + journeyPoints,
      };
    };

    const previous = statsForIds(profileBadgeIDs);
    const current = statsForIds(enabledBadges);
    return {
      previous,
      current,
      delta: {
        badges: current.badges - previous.badges,
        journeys: current.journeys - previous.journeys,
        badgePoints: current.badgePoints - previous.badgePoints,
        journeyPoints: current.journeyPoints - previous.journeyPoints,
        total: current.total - previous.total,
      },
    };
  }, [profileBadgeIDs, enabledBadges, badgesMap]);

  const sortedNewBadges = useMemo(
    () => [...newlyAddedBadges].sort((a, b) => compareBadgesByScoreAsc(a, b) || a.name.localeCompare(b.name)),
    [newlyAddedBadges]
  );

  const nameChanged = !!profile && displayName.trim() !== profile.name;

  const [pasteCode, setPasteCode] = useState('');
  const confirmTopRef = useRef<HTMLDivElement | null>(null);
  const newBadgesRowRef = useRef<HTMLDivElement | null>(null);
  const [newBadgesCapacity, setNewBadgesCapacity] = useState<number | null>(null);
  const [newBadgesExpanded, setNewBadgesExpanded] = useState(false);

  const measureNewBadgesCapacity = useCallback(() => {
    const row = newBadgesRowRef.current;
    if (!row) return;

    const maxHeight = window.innerHeight * NEW_BADGES_MAX_VIEWPORT_RATIO;
    const styles = getComputedStyle(row);
    const gapX = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;
    const gapY = parseFloat(styles.rowGap) || parseFloat(styles.gap) || 0;

    const sample = row.querySelector('.badge') as HTMLElement | null;
    let cellW: number;
    let cellH: number;
    if (sample) {
      const rect = sample.getBoundingClientRect();
      cellW = rect.width;
      cellH = rect.height;
    } else {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      cellW = NEW_BADGES_CELL_REM * rootPx;
      cellH = NEW_BADGES_CELL_REM * rootPx;
    }

    if (row.clientWidth <= 0 || cellW <= 0 || cellH <= 0 || maxHeight <= 0) return;

    const perRow = Math.max(1, Math.floor((row.clientWidth + gapX) / (cellW + gapX)));
    const maxRows = Math.max(1, Math.floor((maxHeight + gapY) / (cellH + gapY)));
    setNewBadgesCapacity(perRow * maxRows);
  }, []);

  const { visibleNewBadges, hiddenNewBadges } = useMemo(() => {
    const capacity = newBadgesCapacity;
    if (capacity == null || sortedNewBadges.length <= capacity) {
      return { visibleNewBadges: sortedNewBadges, hiddenNewBadges: [] as Badge[] };
    }
    const visibleCount = Math.max(1, capacity - 1);
    return {
      visibleNewBadges: sortedNewBadges.slice(0, visibleCount),
      hiddenNewBadges: sortedNewBadges.slice(visibleCount),
    };
  }, [sortedNewBadges, newBadgesCapacity]);

  useLayoutEffect(() => {
    if (claimStep !== 'confirm' || sortedNewBadges.length === 0) {
      setNewBadgesCapacity(null);
      setNewBadgesExpanded(false);
      return;
    }
    measureNewBadgesCapacity();
    const row = newBadgesRowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(() => measureNewBadgesCapacity());
    observer.observe(row);
    window.addEventListener('resize', measureNewBadgesCapacity);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measureNewBadgesCapacity);
    };
  }, [claimStep, sortedNewBadges.length, measureNewBadgesCapacity]);

  useEffect(() => {
    if (hiddenNewBadges.length === 0) setNewBadgesExpanded(false);
  }, [hiddenNewBadges.length]);

  const toggleNewBadgesExpanded = useCallback(() => {
    setNewBadgesExpanded((prev) => !prev);
  }, []);

  useEffect(() => {
    if (claimStep !== 'confirm') return;
    if (profile ? updateDisabled : createDisabled) return;
    if (pasteCode) return;
    const code = clipboardText(effectiveDisplayName);
    setPasteCode(code);
    void writeClipboardText(code);
  }, [claimStep, profile, updateDisabled, createDisabled, pasteCode, effectiveDisplayName, clipboardText]);

  const claimLabel = useMemo(() => {
    if (saveDisabled && !nameChanged) return 'Claim badges';

    const added = newlyAddedBadges.length;
    const journeys = newlyCompletedJourneys.length;
    const sentences: string[] = [];

    if (added > 0 && journeys > 0) {
      sentences.push(`Claim ${added} badge${added === 1 ? '' : 's'}, ${journeys} journey${journeys === 1 ? '' : 's'}`);
    } else if (added > 0) {
      sentences.push(`Claim ${added} badge${added === 1 ? '' : 's'}`);
    } else if (journeys > 0) {
      sentences.push(`Complete ${journeys} journey${journeys === 1 ? '' : 's'}`);
    }

    if (nameChanged) {
      sentences.push('Update display name');
    }

    return sentences.length > 0 ? sentences.join('. ') : 'Claim badges';
  }, [saveDisabled, newlyAddedBadges.length, newlyCompletedJourneys.length, nameChanged]);

  const summaryContinueLabel = 'Summarize & Claim →';

  const formatDelta = (n: number) => (n > 0 ? `+${n}` : `${n}`);

  const leaveEdit = () => {
    setPendingClaimBadgeId(null);
    onInitialAutoClaimConsumed?.();
    // App's navigation blocker prompts once when there are unsaved changes.
    if (profile) {
      navigate(`/${getFirstPartOfEmail(profile.email)}`);
    } else {
      navigate('/');
    }
  };

  const openConfirmStep = useCallback(async () => {
    if (profile ? updateDisabled : createDisabled) return;
    const code = clipboardText(effectiveDisplayName);
    setPasteCode(code);
    navigate(SUMMARY_PATH);
    await writeClipboardText(code);
  }, [profile, updateDisabled, createDisabled, clipboardText, effectiveDisplayName, navigate]);

  // Scroll to top when entering the summarize step (incl. browser back/forward).
  useLayoutEffect(() => {
    if (claimStep !== 'confirm') return;
    confirmTopRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [claimStep]);

  const finalizeClaim = useCallback(async () => {
    const code = pasteCode || clipboardText(effectiveDisplayName);
    await writeClipboardText(code);
    window.open(NEW_PASTE_URL, '_blank');
    onSubmissionTriggered(code);
  }, [pasteCode, clipboardText, effectiveDisplayName, onSubmissionTriggered]);

  const backToEdit = useCallback(() => {
    setEnabledBadgesMap(enabledBadgesMap);
    setPendingClaimBadgeId(null);
    onInitialAutoClaimConsumed?.();
    navigate(editPath);
  }, [enabledBadgesMap, setEnabledBadgesMap, onInitialAutoClaimConsumed, navigate, editPath]);

  const nameHint =
    !displayName.trim() && !defaultDisplayName
      ? 'Enter a display name'
      : displayName.includes(',')
        ? 'Name can’t include a comma'
        : null;
  const badgeHint = !nameHint && enabledBadges.length === 0 ? 'Claim at least one badge' : null;
  const showCreateHints = !profile && createTouched && createDisabled;

  if (claimStep === 'confirm') {
    return (
      <div className="gallery-page claim-badges-page claim-badges-page--confirm" ref={confirmTopRef}>
        <header className="claim-badges-header">
          <div className="claim-badges-header-top">
            <div className="claim-badges-header-start">
              <div className="claim-badges-titles">
                <h1 className="gallery-title">Summarize &amp; Claim</h1>
                <p className="gallery-subtitle">Make it official.</p>
              </div>

              <section className="claim-confirm-score" aria-label="Score change">
                <div className="claim-confirm-score-pair">
                  <div className="claim-confirm-score-block">
                    <span className="claim-confirm-score-label">Before</span>
                    <span className="claim-confirm-score-value">{scoreDiff.previous.total}</span>
                    <span className="claim-confirm-score-unit">pts</span>
                  </div>
                  <span className="claim-confirm-score-arrow" aria-hidden>
                    →
                  </span>
                  <div className="claim-confirm-score-block claim-confirm-score-block--current">
                    <span className="claim-confirm-score-label">After</span>
                    <span className="claim-confirm-score-value">{scoreDiff.current.total}</span>
                    <span className="claim-confirm-score-unit">pts</span>
                  </div>
                  <div className={`claim-confirm-score-delta${scoreDiff.delta.total > 0 ? ' is-up' : ''}`}>
                    {formatDelta(scoreDiff.delta.total)}
                  </div>
                </div>
                <div className="claim-confirm-score-breakdown">
                  <span>
                    Badges {scoreDiff.previous.badges}
                    <span className="claim-confirm-score-sep">→</span>
                    {scoreDiff.current.badges}
                    <span className="claim-confirm-score-chip">{formatDelta(scoreDiff.delta.badges)}</span>
                  </span>
                  <span>
                    Journeys {scoreDiff.previous.journeys}
                    <span className="claim-confirm-score-sep">→</span>
                    {scoreDiff.current.journeys}
                    <span className="claim-confirm-score-chip">{formatDelta(scoreDiff.delta.journeys)}</span>
                  </span>
                </div>
              </section>
            </div>

            <div className="claim-badges-header-end">
              <div className="claim-badges-header-actions">
                {/* Invisible twin of the Claim button — keeps header height identical. */}
                <div className="claim-badges-submit-wrap claim-badges-header-slot" aria-hidden>
                  <button type="button" className="claim-badges-submit" tabIndex={-1} disabled>
                    <SlidingButtonLabel text={summaryContinueLabel} />
                  </button>
                </div>
                <button type="button" className="claim-badges-leave" onClick={backToEdit}>
                  Back to editing
                </button>
              </div>

              <div className="claim-badges-identity">
                <div className="claim-badges-name-field">
                  <label>
                    <span>Display name</span>
                    <input
                      type="text"
                      value={displayName}
                      placeholder={profile ? profile.name : defaultDisplayName || 'Your display name'}
                      onChange={(e) => {
                        const next = e.target.value;
                        setDisplayName(next);
                        if (!profile) setSaveDisabled(false);
                        const nextCode = clipboardText(next.trim() || defaultDisplayName);
                        setPasteCode(nextCode);
                        void writeClipboardText(nextCode);
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="claim-confirm-body">
          {sortedNewBadges.length > 0 ? (
            <section className="claim-badges-diff-section" aria-label="New badges">
              <header className="claim-badges-diff-section-head">
                <h3 className="claim-badges-diff-section-title">New badges</h3>
                <span className="claim-badges-diff-section-meta">
                  {sortedNewBadges.length} added · {formatDelta(scoreDiff.delta.badgePoints)} pts
                </span>
              </header>
              <div className="claim-badges-diff-badge-row" ref={newBadgesRowRef}>
                {visibleNewBadges.map((badge) => (
                  <BadgeComponent
                    key={badge.id}
                    badge={badge}
                    onClick={onSelectBadge}
                    blackAndWhite={false}
                    display
                    smaller
                    showFloatingNameShadow={false}
                    showNameHeight
                  />
                ))}
                {hiddenNewBadges.length > 0 ? (
                  <>
                    <div
                      className={`claim-badges-diff-more${newBadgesExpanded ? ' is-expanded' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={newBadgesExpanded}
                      aria-label={`${hiddenNewBadges.length} hidden. ${
                        newBadgesExpanded ? 'Collapse' : 'Expand'
                      } to ${newBadgesExpanded ? 'hide' : 'review'}.`}
                      onClick={toggleNewBadgesExpanded}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleNewBadgesExpanded();
                        }
                      }}
                    >
                      <span className="claim-badges-diff-more-tile">
                        <span className="claim-badges-diff-more-count">{hiddenNewBadges.length}</span>
                        <span className="claim-badges-diff-more-label">
                          {newBadgesExpanded ? 'hidden · hide' : 'hidden'}
                        </span>
                      </span>
                    </div>
                    {newBadgesExpanded
                      ? hiddenNewBadges.map((badge) => (
                          <BadgeComponent
                            key={badge.id}
                            badge={badge}
                            onClick={onSelectBadge}
                            blackAndWhite={false}
                            display
                            smaller
                            showFloatingNameShadow={false}
                            showNameHeight
                          />
                        ))
                      : null}
                  </>
                ) : null}
              </div>
            </section>
          ) : null}

          {newlyCompletedJourneys.length > 0 ? (
            <section className="claim-badges-diff-section" aria-label="Completed journeys">
              <header className="claim-badges-diff-section-head">
                <h3 className="claim-badges-diff-section-title">Journeys completed</h3>
                <span className="claim-badges-diff-section-meta">
                  {newlyCompletedJourneys.length} finished · {formatDelta(scoreDiff.delta.journeyPoints)} pts
                </span>
              </header>
              <div className="claim-badges-diff-journey-row">
                {newlyCompletedJourneys.map((journey) => (
                  <div
                    key={journey.id}
                    className={`claim-badges-diff-journey rarity-${journey.rarity}`}
                    title={`${journey.name} · ${journey.rarity}`}
                  >
                    <span className="claim-badges-diff-journey-emoji" aria-hidden>
                      {journey.emoji}
                    </span>
                    <span className="claim-badges-diff-journey-name">{journey.name}</span>
                    <span className="claim-badges-diff-journey-rarity" aria-label={journey.rarity}>
                      <img
                        className="claim-badges-diff-journey-rarity-img"
                        src={getRarityImageUrlByName(journey.rarity)}
                        alt=""
                        aria-hidden
                      />
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <div className="claim-confirm-actions">
            <p className="claim-confirm-profile-name">{effectiveDisplayName || 'Your profile'}</p>
            <button type="button" className="claim-badges-submit claim-confirm-finalize" onClick={finalizeClaim}>
              <SlidingButtonLabel text={claimLabel} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-page claim-badges-page">
      <header className="claim-badges-header">
        <div className="claim-badges-header-top">
          <div className="claim-badges-header-start">
            <div className="claim-badges-titles">
              <h1 className="gallery-title">{profile ? 'Edit Profile' : 'Create Profile'}</h1>
              <p className="gallery-subtitle">Tap badges to claim.</p>
            </div>

            <div className="claim-badges-filter-bar">
              <div className={`claim-badges-search-field${badgeSearch ? ' has-clear' : ''}`}>
                <label htmlFor="claim-badge-search" className="claim-badges-search-label">
                  <span className="visually-hidden">Search badges</span>
                  <span className="claim-badges-search-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <circle cx="11" cy="11" r="7" />
                      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    ref={badgeSearchInputRef}
                    id="claim-badge-search"
                    type="search"
                    value={badgeSearch}
                    placeholder="Search by name, description, or group…"
                    autoComplete="off"
                    onChange={(e) => {
                      const next = e.target.value;
                      setBadgeSearch(next);
                      if (next.trim()) setActiveGroup('all');
                    }}
                  />
                  {badgeSearch ? (
                    <button
                      type="button"
                      className="claim-badges-search-clear"
                      aria-label="Clear search"
                      onClick={() => {
                        setBadgeSearch('');
                        badgeSearchInputRef.current?.focus();
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </label>
              </div>
            </div>
          </div>

          <div className="claim-badges-header-end">
            <div className="claim-badges-header-actions">
              {profile ? (
                <div className="claim-badges-submit-wrap">
                  <button
                    type="button"
                    className={`claim-badges-submit ${updateDisabled ? 'disabled' : ''}`}
                    onClick={() => void openConfirmStep()}
                    disabled={updateDisabled}
                    aria-label={summaryContinueLabel}
                  >
                    <SlidingButtonLabel text={summaryContinueLabel} />
                  </button>
                </div>
              ) : (
                <div className="claim-badges-create-wrap claim-badges-submit-wrap">
                  <CreateProfileButton
                    createDisabled={createDisabled}
                    displayName={effectiveDisplayName}
                    enabledBadges={enabledBadges}
                    onSubmissionTriggered={onSubmissionTriggered}
                    setCreateTouched={setCreateTouched}
                    onActivate={() => void openConfirmStep()}
                    label={summaryContinueLabel}
                  />
                  {showCreateHints && badgeHint ? (
                    <p className="claim-badges-hint" role="alert">
                      {badgeHint}
                    </p>
                  ) : null}
                </div>
              )}
              <button type="button" className="claim-badges-leave" onClick={leaveEdit}>
                {profile ? 'Leave edit mode' : 'Cancel'}
              </button>
            </div>

            <div className="claim-badges-identity">
              <div className="claim-badges-name-field">
                <label>
                  <span>Display name</span>
                  <input
                    type="text"
                    value={displayName}
                    placeholder={profile ? profile.name : defaultDisplayName || 'Your display name'}
                    aria-invalid={showCreateHints && !!nameHint}
                    aria-describedby={showCreateHints && nameHint ? 'claim-display-name-hint' : undefined}
                    onChange={(e) => {
                      setDisplayName(e.target.value);
                      if (!profile) setSaveDisabled(false);
                    }}
                  />
                </label>
                {showCreateHints && nameHint ? (
                  <p id="claim-display-name-hint" className="claim-badges-hint" role="alert">
                    {nameHint}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="gallery-nav claim-badges-nav" role="tablist" aria-label="Badge categories">
        {navItems.map((item) => {
          const pct = item.percentage ?? 0;
          const hasFill = Boolean(item.gradient) && pct > 0;
          const clipPath = `polygon(0 0, ${pct}% 0, ${pct + 20}% 100%, 0 100%)`;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={activeGroup === item.id}
              aria-disabled={item.disabled || undefined}
              disabled={item.disabled}
              className={`gallery-nav-chip ${activeGroup === item.id ? 'active' : ''}${
                item.gradient != null ? ' gallery-nav-chip--progress' : ''
              }${item.disabled ? ' gallery-nav-chip--disabled' : ''}${
                item.id !== 'all' ? ' gallery-nav-chip--category' : ''
              }`}
              onClick={() => {
                if (item.disabled) return;
                setActiveGroup(item.id);
                if (item.id !== 'all') setBadgeSearch('');
              }}
            >
              {hasFill ? (
                <span
                  className="gallery-nav-chip-fill"
                  aria-hidden
                  style={{
                    background: item.gradient,
                    opacity: 0.22,
                    clipPath,
                  }}
                />
              ) : null}
              <span className="gallery-nav-chip-label">{item.label}</span>
            </button>
          );
        })}
      </div>

      <BadgeGroupsComponent
        key={leaveEditModeKey}
        profileBadgeIDs={profileBadgeIDs}
        badgesMap={badgesMap}
        mode="editing_profile"
        onClickBadge={onSelectBadge}
        setSaveDisabled={setSaveDisabled}
        setEnabledBadgesForClipboard={() => {}}
        showPercentages
        badgeCreditsSubmissions={creditIds}
        badgeDesignSubmissions={designIds}
        email={profile?.email ?? user?.email ?? ''}
        enabledBadgesMap={enabledBadgesMap}
        setEnabledBadgesMap={setEnabledBadgesMap}
        handleBadgeToggle={handleBadgeToggle}
        editLayoutFrozenPrefix={editLayoutFrozenPrefix}
        visibleGroups={visibleGroups}
        showFreeformBadges={showFreeformBadges}
        showJourneyCompletingSection={showJourneyCompletingSection}
        showClaimedByOthersSection={showClaimedByOthersSection}
        showRecentlyAddedSection={showRecentlyAddedSection}
        profileLastUpdated={profile?.lastUpdated ?? null}
        badgeSearchQuery={badgeSearch}
      />
    </div>
  );
};
